import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// Sezioni Social guidate da configurazione: nessuna business logic nel frontend.

export function renderField(field, value, setValue, testidPrefix) {
  if (field.type === "select") {
    return (
      <Select value={value != null && value !== "" ? String(value) : ""}
        onValueChange={(v) => setValue(field.key, field.numeric ? Number(v) : v)}>
        <SelectTrigger data-testid={`${testidPrefix}-${field.key}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => (
            <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "boolean") {
    return (
      <Select value={value === true ? "true" : value === false ? "false" : ""}
        onValueChange={(v) => setValue(field.key, v === "true")}>
        <SelectTrigger data-testid={`${testidPrefix}-${field.key}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="true">Sì</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "textarea") {
    return <Textarea rows={2} value={value || ""} onChange={(e) => setValue(field.key, e.target.value)} data-testid={`${testidPrefix}-${field.key}`} />;
  }
  return (
    <Input type={field.type === "number" ? "number" : "text"} step="any"
      value={value ?? ""}
      onChange={(e) => setValue(field.key, field.type === "number" ? (e.target.value === "" ? null : e.target.valueAsNumber) : e.target.value)}
      data-testid={`${testidPrefix}-${field.key}`} />
  );
}

export function formatValue(field, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (field.type === "boolean") return value ? "Sì" : "No";
  if (field.type === "select") {
    const opt = field.options.find((o) => String(o.value) === String(value));
    return opt ? opt.label : value;
  }
  return value;
}

export function SingletonSection({ companyId, periodId, endpoint, title, description, fields,
  canWrite, onChanged, testid, summaryRender }) {
  const [record, setRecord] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    if (!companyId || !periodId) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${periodId}/social/${endpoint}`);
      setRecord(data.record);
    } catch (err) {
      if (err?.response?.status !== 403) {
        toast.error("Errore caricamento", { description: formatApiError(err) });
      }
    }
  }, [companyId, periodId, endpoint]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put(
        `/companies/${companyId}/periods/${periodId}/social/${endpoint}`, { data: form });
      toast.success("Dati salvati e KPI ricalcolati");
      setOpen(false);
      setRecord(data.record);
      if (onChanged) onChanged(data.summary);
    } catch (err) {
      toast.error("Errore salvataggio", { description: formatApiError(err) });
    }
  };

  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</p>
          </div>
          <div className="flex items-center gap-2">
            {record?.source_type && (
              <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200 text-[11px]" data-testid={`${testid}-source`}>
                {record.source_type === "JOBTIMEHR_MOCK" ? "JOBTIMEHR — MOCK" : record.source_type}
              </Badge>
            )}
            {canWrite && (
              <Button size="sm" variant="outline"
                onClick={() => { setForm(record || {}); setOpen(true); }}
                data-testid={`${testid}-edit`}>
                <Pencil className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> {record ? "Modifica" : "Inserisci"}
              </Button>
            )}
          </div>
        </div>
        {summaryRender ? summaryRender(record) : (
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            {fields.map((f) => (
              <div key={f.key}>
                <dt className="text-xs text-slate-500">{f.label}</dt>
                <dd className="font-medium text-slate-900" data-testid={`${testid}-value-${f.key}`}>
                  {formatValue(f, record?.[f.key])}
                </dd>
              </div>
            ))}
          </dl>
        )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{description} Solo dati aggregati: nessun dato nominativo.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.key} className={`space-y-1.5 ${f.full ? "md:col-span-2" : ""}`}>
                  <Label>{f.label}{f.required ? " *" : ""}</Label>
                  {renderField(f, form[f.key], (k, v) => setForm((prev) => ({ ...prev, [k]: v })), `${testid}-field`)}
                  {f.hint && <p className="text-xs text-slate-500">{f.hint}</p>}
                </div>
              ))}
              <DialogFooter className="md:col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid={`${testid}-cancel`}>Annulla</Button>
                <Button type="submit" className="bg-green-700 hover:bg-green-800" data-testid={`${testid}-submit`}>Salva e ricalcola</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export function ListSection({ companyId, periodId, endpoint, title, description, columns, fields,
  canWrite, onChanged, testid, addLabel = "Aggiungi riga" }) {
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    if (!companyId || !periodId) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${periodId}/social/${endpoint}`);
      setRecords(data.records || []);
    } catch (err) {
      toast.error("Errore caricamento", { description: formatApiError(err) });
    }
  }, [companyId, periodId, endpoint]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${periodId}/social/${endpoint}`, { data: form });
      toast.success("Riga aggiunta");
      setOpen(false);
      setForm({});
      load();
      if (onChanged) onChanged(data.summary);
    } catch (err) {
      toast.error("Errore salvataggio", { description: formatApiError(err) });
    }
  };

  const remove = async (rec) => {
    try {
      const { data } = await api.delete(
        `/companies/${companyId}/periods/${periodId}/social/${endpoint}/${rec.id}`);
      toast.success("Riga eliminata");
      load();
      if (onChanged) onChanged(data.summary);
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500 max-w-2xl leading-relaxed">{description}</p>
        {canWrite && (
          <Button size="sm" className="bg-green-700 hover:bg-green-800"
            onClick={() => { setForm({}); setOpen(true); }} data-testid={`${testid}-add`}>
            <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> {addLabel}
          </Button>
        )}
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid={`${testid}-table`}>
            <TableHeader>
              <TableRow>
                {columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
                <TableHead>Fonte</TableHead>
                {canWrite && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 2} className="text-center text-slate-500 py-8" data-testid={`${testid}-empty`}>
                    Nessun dato inserito per questo periodo.
                  </TableCell>
                </TableRow>
              )}
              {records.map((r) => (
                <TableRow key={r.id} data-testid={`${testid}-row-${r.id.slice(0, 8)}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</TableCell>
                  ))}
                  <TableCell>
                    <Badge variant="outline" className="bg-slate-50 text-slate-600 text-[11px]">
                      {r.source_type === "JOBTIMEHR_MOCK" ? "JOBTIMEHR — MOCK" : (r.source_type || "MANUAL")}
                    </Badge>
                  </TableCell>
                  {canWrite && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="text-red-700" onClick={() => remove(r)} data-testid={`${testid}-delete-${r.id.slice(0, 8)}`}>
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle>{addLabel}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={`space-y-1.5 ${f.full ? "md:col-span-2" : ""}`}>
                <Label>{f.label}{f.required ? " *" : ""}</Label>
                {renderField(f, form[f.key], (k, v) => setForm((prev) => ({ ...prev, [k]: v })), `${testid}-field`)}
                {f.hint && <p className="text-xs text-slate-500">{f.hint}</p>}
              </div>
            ))}
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid={`${testid}-cancel`}>Annulla</Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" data-testid={`${testid}-submit`}>Salva e ricalcola</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
